import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export type SectionName = "assets" | "customers" | "systems" | "projects" | "planner" | "orders" | "filling" | "alerts" | "dashboards" | "connections"

interface SectionState {
  activeSection: SectionName | null
  lastActiveSection: SectionName | null
  mountedSections: SectionName[]
  sectionSubRoutes: Record<SectionName, string>
  sectionReloadTriggers: Record<SectionName, number>
}

const initialState: SectionState = {
  activeSection: null,
  lastActiveSection: null,
  mountedSections: [],
  sectionSubRoutes: {} as Record<SectionName, string>,
  sectionReloadTriggers: {} as Record<SectionName, number>,
}

const sectionSlice = createSlice({
  name: "section",
  initialState,
  reducers: {
    setActiveSection: (state, action: PayloadAction<{ section: SectionName | null; subRoute?: string }>) => {
      // Save the current active section as last active before changing
      if (state.activeSection && action.payload.section !== state.activeSection) {
        state.lastActiveSection = state.activeSection
      }

      state.activeSection = action.payload.section
      // Add to mounted sections when activated
      if (action.payload.section && !state.mountedSections.includes(action.payload.section)) {
        state.mountedSections.push(action.payload.section)
      }
      // Track sub-route for the section
      if (action.payload.section && action.payload.subRoute !== undefined) {
        state.sectionSubRoutes[action.payload.section] = action.payload.subRoute
      }
    },
    mountSection: (state, action: PayloadAction<SectionName>) => {
      if (!state.mountedSections.includes(action.payload)) {
        state.mountedSections.push(action.payload)
      }
    },
    unmountSection: (state, action: PayloadAction<SectionName>) => {
      state.mountedSections = state.mountedSections.filter((section) => section !== action.payload)
      // If we're unmounting the active section, clear it
      if (state.activeSection === action.payload) {
        state.activeSection = null
      }
    },
    setSectionSubRoute: (state, action: PayloadAction<{ section: SectionName; subRoute: string }>) => {
      state.sectionSubRoutes[action.payload.section] = action.payload.subRoute
    },
    forceSectionReload: (state, action: PayloadAction<SectionName>) => {
      // Increment the reload trigger to force section re-initialization
      state.sectionReloadTriggers[action.payload] = (state.sectionReloadTriggers[action.payload] || 0) + 1
    },
    clearSections: (state) => {
      state.activeSection = null
      state.lastActiveSection = null
      state.mountedSections = []
      state.sectionSubRoutes = {} as Record<SectionName, string>
      state.sectionReloadTriggers = {} as Record<SectionName, number>
    },
  },
})

export const { setActiveSection, mountSection, unmountSection, setSectionSubRoute, forceSectionReload, clearSections } =
  sectionSlice.actions
export default sectionSlice.reducer
